import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicesCapabilities.css';
import servicesData from '../data/servicesData';

function Services() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Capabilities — TQNY ALBYANAT";
  }, []);

  const mainServices = servicesData.slice(0, 5);

  return (
    <>
      {/* PAGE HERO */}
      <section className="svc-page-hero">
        <div className="svc-container">
          <nav className="svc-breadcrumb">
            <a href="/" onClick={() => navigate('/')} className="svc-breadcrumb-link">Home</a>
            <span className="svc-breadcrumb-sep">/</span>
            <span className="svc-breadcrumb-current">Capabilities</span>
          </nav>
          <h1 className="svc-page-title">
            Comprehensive Capabilities <br /> for Digital Enterprise Transformation
          </h1>
          <p className="svc-page-subtitle">
            From data strategy and cloud migration to AI enablement and cybersecurity, we deliver integrated solutions across five core capability areas—each with proven methodology, global talent, and local expertise.
          </p>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      {mainServices.map((service, idx) => {
        const outcomes = [
          // Service 1: Enterprise Data & AI
          [
            { stat: "200", emphasis: "+", label: "Data sources unified, typical engagement" },
            { stat: "60", emphasis: "%", label: "Faster reporting cycles, average" },
            { stat: "100", emphasis: "%", label: "Regulatory compliance, every project" }
          ],
          // Service 2: Technology & Cloud Services
          [
            { stat: "At", emphasis: "-Globe", label: "Data residency on every engagement" },
            { stat: "3", emphasis: "x", label: "Faster deployment cycles, post-migration" },
            { stat: "40", emphasis: "%", label: "Average cloud cost reduction via FinOps" }
          ],
          // Service 3: Advanced Analytics & AI Enablement
          [
            { stat: "AR", emphasis: "+EN", label: "Native bilingual NLP capability" },
            { stat: "SDAIA", emphasis: "", label: "Aligned with national AI frameworks" },
            { stat: "95", emphasis: "%+", label: "Typical model accuracy on production tasks" }
          ],
          // Service 4: Cloud Acceleration Studio (assumed similar pattern)
          [
            { stat: "Multi", emphasis: "-Cloud", label: "Hyperscaler agnostic deployment" },
            { stat: "60", emphasis: "%", label: "Faster time-to-market for AI workloads" },
            { stat: "SRE", emphasis: "", label: "Managed with SLA guarantees" }
          ],
          // Service 5: Cybersecurity & Compliance
          [
            { stat: "24", emphasis: "/7", label: "SOC monitoring, in-Kingdom" },
            { stat: "100", emphasis: "%", label: "NCA-ECC alignment standard" },
            { stat: "<15", emphasis: " min", label: "Critical incident response time" }
          ]
        ];

        const serviceOutcomes = outcomes[idx] || [];

        return (
          <section
            key={idx}
            className={`svc-service-block ${idx % 2 === 1 ? 'svc-service-block-reverse' : ''}`}
          >
            <div className="svc-container">
              <div className="svc-service-block-grid">
                <div className="svc-service-detail">
                  <div className="svc-service-num-large">{String(idx + 1).padStart(2, '0')}</div>
                  <h2 className="svc-service-block-title">{service.title}</h2>
                  <p className="svc-service-block-lead">{service.description}</p>

                  {/* Service Outcomes */}
                  <div className="svc-service-outcomes">
                    {serviceOutcomes.map((outcome, outIdx) => (
                      <div key={outIdx}>
                        <div className="svc-outcome-stat">
                          {outcome.stat}{outcome.emphasis && <em>{outcome.emphasis}</em>}
                        </div>
                        <div className="svc-outcome-label">{outcome.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="svc-service-meta">
                  <div className="svc-service-meta-card">
                    <div className="svc-service-meta-label">What We Deliver</div>
                    <ul className="svc-service-meta-list">
                      {service.subServices.map((sub, subIdx) => (
                        <li key={subIdx}>{sub.title}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* SERVICE 06 - ADVISORY & CAPABILITY */}
      <section className="svc-service-block">
        <div className="svc-container">
          <div className="svc-service-block-grid svc-service-block-reverse-06">
            <div className="svc-service-meta">
              <div className="svc-service-meta-card">
                <div className="svc-service-meta-label">What We Deliver</div>
                <ul className="svc-service-meta-list">
                  <li>Strategic technology & digital advisory</li>
                  <li>Technology roadmap & target architecture</li>
                  <li>Transformation program management</li>
                  <li>Capability assessments & gap analysis</li>
                  <li>Managed services & SLA agreements</li>
                  <li>Training, mentoring & knowledge transfer</li>
                  <li>Saudi Talent recruitment & placement</li>
                  <li>Centers of Excellence (CoE) setup</li>
                </ul>
              </div>
            </div>
            <div className="svc-service-detail">
              <div className="svc-service-num-large">06</div>
              <h2 className="svc-service-block-title">Advisory & Capability.</h2>
              <p className="svc-service-block-lead">Strategic advisory and managed services that deliver outcomes today while building lasting in-country capability for tomorrow.</p>
              <p className="svc-service-block-body">Not every engagement is a build. Sometimes the question is "what should we build?" — or "how do we run what we already have?" Our advisory practice helps clients answer those questions with clarity, then provides the program management, training, and managed services to execute. Knowledge transfer and capability building are standard on every engagement: we want your team to be stronger when we leave than when we arrived.</p>
              <div className="svc-service-outcomes">
                <div>
                  <div className="svc-outcome-stat">100<em>%</em></div>
                  <div className="svc-outcome-label">Engagements include capability transfer</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">SLA</div>
                  <div className="svc-outcome-label">Managed services with SLA guarantees</div>
                </div>
                <div>
                  <div className="svc-outcome-stat">In-KSA</div>
                  <div className="svc-outcome-label">Training delivered on the ground</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH SECTION */}
      <section className="svc-approach">
        <div className="svc-container">
          <div className="svc-section-eyebrow">How We Work</div>
          <h2 className="svc-approach-title">A method <em>that delivers.</em></h2>
          <p className="svc-approach-intro">Every engagement follows a deliberate four-phase rhythm — built to surface risk early, deliver value continuously, and leave behind capability that lasts.</p>
          <div className="svc-approach-grid">
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">01</div>
              <div className="svc-approach-step-title">Listen</div>
              <div className="svc-approach-step-desc">We start by understanding the business — not just the technology. Stakeholder workshops, current-state assessment, and outcome alignment.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">02</div>
              <div className="svc-approach-step-title">Design</div>
              <div className="svc-approach-step-desc">Target architecture, delivery roadmap, success metrics. We write down what we'll build, why, and how we'll know it worked.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">03</div>
              <div className="svc-approach-step-title">Deliver</div>
              <div className="svc-approach-step-desc">Iterative delivery in tight cycles. Working software in weeks, not quarters. Continuous feedback, no surprise reveals.</div>
            </div>
            <div className="svc-approach-step">
              <div className="svc-approach-step-num">04</div>
              <div className="svc-approach-step-title">Transfer</div>
              <div className="svc-approach-step-desc">Knowledge transfer is built into every phase. When we hand off, your team owns the system, the documentation, and the playbook.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="svc-cta" id="contact">
        <div className="svc-container">
          <div className="svc-cta-inner">
            <div className="svc-cta-content">
              <div className="svc-cta-eyebrow">Start a Conversation</div>
              <h2>Let's build something that lasts.</h2>
              <p>Every great engagement begins with a conversation. Tell us about your ambition — we'll tell you how we can help.</p>
              <button onClick={() => navigate("/contact")} type="submit" className="svc-btn-cta-primary">Request a Meeting <span>→</span></button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;